import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../i18n';
import { listMyRequestEmails } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { queryKeys } from '../../lib/queryKeys';
import { Chip, Skeleton } from '../../ui/Primitives';
import { ANSWER_EMAIL_STATUS_TONE } from '../admin/access/accessFormat';

/**
 * A REQUEST'S HISTORY on the launcher (spec row 14; FINAL demo "History"): when it was asked
 * and answered, and every answer email Accounts wrote to the person, oldest first, each with
 * its status and its full text one click away. The emails are the person's OWN: the read
 * takes no user parameter, and the server answers only for the caller's own request.
 *
 * Under the `capture` driver an email shows "Captured": it was written and kept, and nothing
 * left the server.
 */
export function RequestHistory({
  kind,
  id,
  askedAt,
  decidedAt,
}: {
  kind: 'catalog' | 'other';
  id: string;
  askedAt: string;
  decidedAt: string | null;
}) {
  const [shown, setShown] = useState<string | null>(null);
  const emails = useQuery({
    queryKey: queryKeys.myRequestEmails(kind, id),
    queryFn: () => listMyRequestEmails(kind, id),
  });

  const line = { display: 'grid', gridTemplateColumns: '130px minmax(0,1fr)', gap: 10, padding: '3px 0 3px 12px', fontSize: 12 } as const;

  return (
    <ol
      style={{
        listStyle: 'none',
        margin: '8px 0 0',
        padding: '0 0 0 2px',
        borderLeft: '2px solid var(--border)',
      }}
    >
      <li style={line}>
        <span className="tx-subtle" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatDateTime(askedAt)}
        </span>
        <span>{STR.answerEmails.youAsked}</span>
      </li>
      {decidedAt === null ? null : (
        <li style={line}>
          <span className="tx-subtle" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDateTime(decidedAt)}
          </span>
          <span>{STR.answerEmails.answered}</span>
        </li>
      )}
      {emails.isLoading ? (
        <li style={line}>
          <span />
          <Skeleton style={{ height: 12, width: 180, borderRadius: 5 }} />
        </li>
      ) : null}
      {emails.isError ? (
        <li style={line}>
          <span />
          <span className="tx-subtle">{STR.errors.couldNotLoad}</span>
        </li>
      ) : null}
      {emails.data?.data.length === 0 && decidedAt !== null ? (
        <li style={line}>
          <span />
          <span className="tx-subtle">{STR.answerEmails.none}</span>
        </li>
      ) : null}
      {(emails.data?.data ?? []).map((email) => {
        const open = shown === email.id;
        return (
          <li key={email.id} style={line}>
            <span className="tx-subtle" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatDateTime(email.sentAt ?? email.createdAt)}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {STR.answerEmails.emailed}
                <Chip tone={ANSWER_EMAIL_STATUS_TONE[email.status]} size="sm">
                  {STR.answerEmails.status[email.status]}
                </Chip>
                <button
                  type="button"
                  className="tbtn"
                  aria-expanded={open}
                  onClick={() => {
                    setShown(open ? null : email.id);
                  }}
                  style={{ padding: 0, border: 'none', fontSize: 12, fontWeight: 650, color: 'var(--accent)' }}
                >
                  {open ? STR.answerEmails.hideEmail : STR.answerEmails.showEmail}
                </button>
              </span>
              {open ? (
                <span
                  style={{
                    display: 'block',
                    marginTop: 6,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid var(--border)',
                    background: 'var(--surface-2)',
                  }}
                >
                  <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5 }}>
                    {STR.answerEmails.to(email.to)}
                  </span>
                  <span style={{ display: 'block', fontWeight: 650, margin: '2px 0 6px' }}>
                    {email.subject}
                  </span>
                  <span style={{ display: 'block', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.55 }}>
                    {email.body}
                  </span>
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
